import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import QuestionCard from "@/components/site/QuestionCard";

const ACCENT = "#F57F17"; // gold

const QUESTIONS = [
  {
    q: "A message says a parcel couldn't be delivered and asks only to confirm the address. Harmless — right?",
    f: "That is stage one of a two-stage trap. The address is the real target; entering it is the actual loss. The small 'redelivery fee' that follows is just the second hook. A genuine service never collects a fee through a link.",
  },
  {
    q: "Someone selling an old phone online is sent a QR code 'to receive the payment.' Would they scan it?",
    f: "Most would. But scanning a QR code only ever sends money — it never receives it. This single misunderstanding drains thousands of accounts every month.",
  },
  {
    q: "A caller claims to be from the CBI, says a parcel in your name contains drugs, and that you are now under 'digital arrest.' Could a calm person be shaken?",
    f: "Many are. But there is no such thing as a digital arrest in Indian law — no agency ever arrests anyone by video call. That one fact, known in advance, defeats the entire scam.",
  },
  {
    q: "A voice on the phone sounds exactly like a close family member, in distress, asking for money urgently. Would the voice be trusted?",
    f: "Nearly 7 in 10 Indians cannot tell an AI-cloned voice from the real one. A few seconds of audio from anywhere online is now enough to clone it convincingly.",
  },
  {
    q: "An investment group shows steady, attractive returns and others clearly profiting. Would the opportunity feel real?",
    f: "Investment scams accounted for the largest share of money lost to cyber fraud in India in 2025. The profits shown are fabricated; the people 'profiting' are part of the trap.",
  },
  {
    q: "A phone suddenly loses all signal for no clear reason. Would anyone treat that as an emergency?",
    f: "Almost no one. But a sudden, unexplained loss of signal can mean a SIM has been swapped — and someone else is now receiving every OTP and password reset.",
  },
];

const CATEGORIES = [
  { t: "Identity & Authority Frauds", d: "Digital arrest, fake government websites, customs and courier scams." },
  { t: "Financial & Investment Frauds", d: "UPI and QR-code scams, fake trading platforms, unordered-delivery (brushing) fraud." },
  { t: "Social Engineering", d: "Fake jobs, romance and dating fraud, online gaming and betting traps." },
  { t: "Technology-Based Frauds", d: "AI voice cloning and deepfakes, SIM-swap attacks, mule-account recruitment." },
  { t: "Response & Recovery", d: "The 30-minute recovery window, reporting through 1930 and the right portals, and a family protection plan." },
];

export default function CyberSafety() {
  useReveal();
  useEffect(() => { document.title = "Cyber Safety — LifeIQ"; }, []);

  return (
    <main>
      {/* HERO */}
      <section className="bg-[#0D1B2A] text-white relative">
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: ACCENT }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#F57F17" }}>
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: ACCENT }} />
              A dedicated LifeIQ module
            </div>
            <h1 className="mt-5 font-serif-d text-[40px] sm:text-6xl lg:text-[62px] leading-[1.06] tracking-tight">
              Education is not protection. Even the most knowledgeable people are being cheated.
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              In 2025, fraudsters took ₹45 lakh from a retired IAS officer, ₹56 lakh from an Army Colonel, and ₹72 lakh from an 82-year-old in Banjara Hills. These are not careless people. The scams exploit fear and trust — not ignorance. If they can reach them, they can reach anyone. This is the module that builds the reflex to stop them.
            </p>
          </div>
        </div>
      </section>

      {/* DISCOVERY QUESTIONS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Read each one slowly.</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              How many of these would catch a sharp, educated person?
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">
              Read each one slowly. The unsettling part is not that these are clever. It is how ordinary and convincing they feel in the moment.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {QUESTIONS.map((c, i) => <QuestionCard key={i} q={c.q} f={c.f} i={i} accent={ACCENT} />)}
          </div>

          <div className="mt-16 max-w-3xl reveal">
            <p className="font-serif-d text-[26px] sm:text-[32px] leading-[1.3]" style={{ color: ACCENT }}>
              If even one of these would have worked — that is the gap. And it is not closed by being smart. It is closed by a specific, practised reflex, learned before the moment arrives.
            </p>
          </div>
        </div>
      </section>

      {/* WHY KNOWLEDGE ISN'T ENOUGH */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Why warnings aren't enough</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Why do educated, careful people still fall for these?
            </h2>
          </div>
          <div className="mt-10 space-y-7 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            <p>
              Because these frauds do not attack what you know. They attack how you feel — fear of the law, trust in authority, love for family, the thrill of a good return. Under that pressure, the calm, rational brain goes quiet. The most law-abiding, authority-respecting people are often the most vulnerable, not the least.
            </p>
            <p>
              This is why a warning is not enough. A person can read about a scam and still freeze when it happens to them. What protects them is a trained reflex — a specific action rehearsed in advance, so it fires before fear takes over. Building that reflex is exactly what this module does.
            </p>
          </div>

          <figure className="mt-14 reveal">
            <blockquote className="font-serif-d italic text-[24px] sm:text-[30px] leading-[1.3] text-[#F57F17] border-l-[3px] border-[#F57F17] pl-7">
              "More than the intelligence of the fraudster, it is the lack of awareness of the victim that often determines the size of the loss."
            </blockquote>
          </figure>
        </div>
      </section>

      {/* WHAT THE MODULE COVERS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>What the module covers</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              16 chapters. Five categories. One protected family.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {CATEGORIES.map((c, i) => (
              <div key={c.t} data-testid={`cyber-cat-${i}`} className="reveal bg-white border border-[#E5E7EA] rounded-sm p-7 flex flex-col" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="h-1 w-10" style={{ backgroundColor: ACCENT }} />
                <div className="mt-5 text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>0{i + 1}</div>
                <h3 className="mt-2 font-serif-d text-[22px] text-[#0D1B2A] leading-tight">{c.t}</h3>
                <p className="mt-3 text-[15px] text-[#5C6470] leading-[1.65]">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE FAMILY EFFECT */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">The family effect</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08] text-white">
              A protected student protects a whole household.
            </h2>
            <p className="mt-7 text-[17px] sm:text-[19px] text-white/85 leading-[1.75]">
              The people most often targeted by these scams are parents and grandparents. A teenager who learns these reflexes becomes the one who recognises a digital-arrest call before an elder pays, who saves the family fraud helpline in everyone's phone, and who explains the QR-code rule at the dinner table. This is the rare subject where teaching one child quietly defends an entire family.
            </p>
            <div className="mt-10 rounded-sm border border-white/15 bg-white/[0.04] p-7">
              <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Two facts worth keeping</div>
              <p className="mt-3 font-serif-d text-[22px] sm:text-[24px] leading-[1.4] text-[#F57F17]">
                A genuine redelivery never asks for payment through a link — and the cyber-fraud helpline 1930 can freeze a fraudulent transfer if called within the first 30 minutes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GROUNDED IN REAL CASES */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Grounded in real cases</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Real cases. Real defences.
            </h2>
          </div>
          <p className="mt-10 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            Every chapter is built on documented fraud from Hyderabad and Andhra Pradesh — reported in Eenadu, Deccan Chronicle, and national press — not on hypothetical examples. Students learn the exact pattern of each scam, the precise moment it can be stopped, and the specific words and actions that stop it. They don't just hear that fraud exists. They rehearse defeating it until the response is automatic.
          </p>

          <figure className="mt-14 reveal">
            <blockquote className="font-serif-d italic text-[22px] sm:text-[28px] leading-[1.35] text-[#F57F17] border-l-[3px] border-[#F57F17] pl-7">
              "The impact of deception is rarely determined by the fraud itself; it is determined by the gap between the complexity of the deception and the individual's ability to recognise it. LifeIQ exists to reduce that gap."
            </blockquote>
          </figure>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-28 text-center">
          <div className="reveal">
            <h2 className="font-serif-d text-[36px] sm:text-5xl lg:text-[58px] leading-[1.06] text-white max-w-3xl mx-auto">
              Give a generation the reflex to stay safe.
            </h2>
            <p className="mt-7 text-[17px] sm:text-[19px] text-white/80 leading-[1.7] max-w-2xl mx-auto">
              The Cyber Safety module can be delivered on its own or as part of the full LifeIQ curriculum. If you lead or know a school, the best first step is a conversation.
            </p>
            <Link to="/contact" data-testid="cyber-cta" className="mt-10 inline-flex items-center gap-2 bg-[#F57F17] text-white px-8 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm">
              Book a Conversation <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
